import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { FaSearch, FaEye } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import AdminNav from '../../components/AdminNav';
import api from '../../utils/api';
import './AdminOrders.css';

const AdminOrders = () => {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    let filtered = orders;

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.user?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order._id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => {
        if (statusFilter === 'delivered') return order.isDelivered;
        if (statusFilter === 'paid') return order.isPaid && !order.isDelivered;
        if (statusFilter === 'pending') return !order.isPaid;
        return true;
      });
    }

    setFilteredOrders(filtered);
  }, [searchTerm, statusFilter, orders]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
      setFilteredOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, updates) => {
    try {
      await api.put(`/orders/${orderId}`, updates);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order');
    }
  };

  const getStatusBadge = (order) => {
    if (order.isDelivered) return <span className="status-badge delivered">Delivered</span>;
    if (order.isPaid) return <span className="status-badge paid">Paid</span>;
    return <span className="status-badge pending">Pending</span>;
  };

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return <div className="admin-loading">Loading orders...</div>;
  }

  return (
    <>
      <AdminNav />
      <div className="admin-orders">
        <div className="admin-header">
        <div>
          <h1>Manage Orders</h1>
          <p>{orders.length} total orders</p>
        </div>
      </div>

      <div className="orders-controls">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by customer name, email, or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Orders</option>
          <option value="pending">Pending Payment</option>
          <option value="paid">Paid</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      <div className="orders-table">
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map(order => (
              <tr key={order._id}>
                <td><strong>#{order._id.substring(0, 8)}</strong></td>
                <td>
                  <div>{order.user?.name}</div>
                  <div className="email-text">{order.user?.email}</div>
                </td>
                <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                <td>${order.totalPrice.toFixed(2)}</td>
                <td>{getStatusBadge(order)}</td>
                <td>
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="btn-icon btn-view"
                  >
                    <FaEye /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredOrders.length === 0 && (
          <div className="no-orders">
            <p>No orders found</p>
          </div>
        )}
      </div>

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Order Details</h2>
            <div className="order-details">
              <div className="detail-section">
                <h3>Customer Information</h3>
                <p><strong>Name:</strong> {selectedOrder.user?.name}</p>
                <p><strong>Email:</strong> {selectedOrder.user?.email}</p>
              </div>
              
              <div className="detail-section">
                <h3>Shipping Address</h3>
                <p>{selectedOrder.shippingAddress.address}</p>
                <p>{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.postalCode}</p>
                <p>{selectedOrder.shippingAddress.country}</p>
              </div>

              <div className="detail-section">
                <h3>Order Items</h3>
                {selectedOrder.orderItems.map((item, index) => (
                  <div key={index} className="order-item">
                    <span>{item.name} x {item.qty}</span>
                    <span>${(item.price * item.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="detail-section">
                <h3>Order Summary</h3>
                <div className="order-item">
                  <span>Items:</span>
                  <span>${selectedOrder.itemsPrice.toFixed(2)}</span>
                </div>
                <div className="order-item">
                  <span>Shipping:</span>
                  <span>${selectedOrder.shippingPrice.toFixed(2)}</span>
                </div>
                <div className="order-item">
                  <span>Tax:</span>
                  <span>${selectedOrder.taxPrice.toFixed(2)}</span>
                </div>
                <div className="order-item total">
                  <strong>Total:</strong>
                  <strong>${selectedOrder.totalPrice.toFixed(2)}</strong>
                </div>
              </div>

              <div className="detail-section">
                <h3>Order Status</h3>
                <div className="status-controls">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedOrder.isPaid}
                      onChange={(e) => updateOrderStatus(selectedOrder._id, { isPaid: e.target.checked })}
                    />
                    Paid
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedOrder.isDelivered}
                      onChange={(e) => updateOrderStatus(selectedOrder._id, { isDelivered: e.target.checked })}
                    />
                    Delivered
                  </label>
                </div>
              </div>
            </div>
            <button onClick={() => setSelectedOrder(null)} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AdminOrders;
